import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { useApiData } from 'lib/api';
import { AdminsTransferRightsDisplayAssets } from 'components/admin/admins';
import {
  Alert,
  LoadingIndicator,
  ModalButtonWithForm
} from 'components/common';
import { FormGroup, RadioGroup, Text } from 'components/common/form';

import type { User } from 'shared-types';
import type { GetAdmins } from 'shared-types/responses';
import type { ButtonSize } from 'components/common/Button';

interface Props {
  buttonIsDropdown?: boolean;
  buttonSize?: ButtonSize;
  handleSuccess: () => Promise<void>;
  sendEmail?: boolean;
  targetUser: Pick<User, 'id' | 'isAnonymous' | 'roles'> & {
    hasOtherCourses?: boolean;
  };
}

const UsersDelete = ({
  buttonIsDropdown,
  buttonSize,
  handleSuccess,
  sendEmail = false,
  targetUser
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { accountAnonymiseOnDeletion, requireReasonWhenDeletingData },
    user
  } = useReduxState();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { data, loading } = useApiData<GetAdmins>({
    url: `/admin/admins?id=${targetUser.id}`,
    onlyIf: Boolean(modalIsOpen) && targetUser?.roles?.includes('admin')
  });

  const deleteUser = async (values: {
    anonymise: boolean;
    deletionReason?: string;
  }) => {
    try {
      const params = new URLSearchParams({
        anonymise: String(values.anonymise || false),
        ...(sendEmail && { sendEmail: 'true' }),
        ...(values.deletionReason && { deletionReason: values.deletionReason })
      });

      await api.delete(`/admin/users/${targetUser.id}?${params.toString()}`);
      await handleSuccess();
      dispatch(
        actions.addNotice({
          type: 'success',
          text: messages.usersDeleteUserSuccess
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const couldAnonymise = accountAnonymiseOnDeletion && !targetUser.isAnonymous;

  return (
    <ModalButtonWithForm
      buttonCategory="danger"
      buttonDisabled={
        targetUser &&
        !user?.roles?.includes('superadmin') &&
        targetUser.roles?.includes('admin')
      }
      buttonIcon="trash-alt"
      buttonIsDropdown={buttonIsDropdown}
      buttonSize={buttonSize}
      buttonText={messages.usersDeleteUser}
      initialValues={couldAnonymise ? { anonymise: true } : {}}
      modalActionText={messages.commonDelete}
      modalTitle={messages.usersDeleteUser}
      onHide={() => setModalIsOpen(false)}
      onShow={() => setModalIsOpen(true)}
      onSubmit={deleteUser}
    >
      {({}) =>
        targetUser?.roles?.includes('admin') && loading ? (
          <LoadingIndicator />
        ) : (
          <div className="content">
            {targetUser.hasOtherCourses ? (
              <Alert
                text={messages.usersDeleteUserConfirmWarning}
                type="warning-light"
              />
            ) : null}

            <AdminsTransferRightsDisplayAssets
              assets={data?.users?.[0].administeredContent}
              label={messages.usersDeleteUserExclusiveAssetsWarning}
              showJustOnlyOwner={true}
            />

            <div className="info">
              {couldAnonymise
                ? messages.usersDeleteUserConfirm
                : `${messages.usersDeleteUserInfo} ${messages.usersDeleteUserConfirm}`}
            </div>

            {couldAnonymise ? (
              <FormGroup>
                <RadioGroup
                  field="anonymise"
                  id={`anonymise-${targetUser?.id}`}
                  labelStyle={{ fontWeight: 600 }}
                  options={[
                    {
                      id: 'anonymise',
                      label: messages.usersAnonymise,
                      labelAdditionalContent: messages.usersDeleteUserAnonymise,
                      value: true
                    },
                    {
                      id: 'delete',
                      label: messages.usersDeleteUser,
                      labelAdditionalContent: messages.usersDeleteUserInfo,
                      value: false
                    }
                  ]}
                  spacing="10px"
                />
              </FormGroup>
            ) : null}

            {requireReasonWhenDeletingData ? (
              <FormGroup
                htmlFor={`deletionReason-${targetUser?.id}`}
                label={'Deletion reason'}
              >
                <Text
                  field="deletionReason"
                  id={`deletionReason-${targetUser?.id}`}
                  maxLength={255}
                  autocomplete="off"
                  placeholder={messages.commonInputDeletionReason}
                  required={true}
                />
              </FormGroup>
            ) : null}

            <style jsx>{`
              .content {
                display: flex;
                flex-direction: column;
                gap: 15px;
              }
              .resources {
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-bottom: 20px;
              }
              .info {
                font-size: 1.5rem;
                line-height: 1.5;
              }
            `}</style>
          </div>
        )
      }
    </ModalButtonWithForm>
  );
};

export default UsersDelete;
